<script setup lang="ts">
import type { IncorporationLine, RequirementSummary } from '@/api/erp-integration';
import SectionHeader from '@/components/section-header.vue';
import InfoField from '@/components/info-field.vue';
import { type LineDraft, type RowDraft } from './erp-line-draft';
import { formatDate, quantityText, requirementText } from './erp-format';

// The incorporation screen's last step: what confirming will do, read back
// before it is done.

export interface PatientSummary {
  name: string;
  // Only for a new patient.
  dni: string | null;
  address: string | null;
  // Only for a new patient: who pays the Sales orders in Odoo.
  representative: string | null;
  goLiveOn: string;
  incorporatedBefore: boolean;
}

const props = defineProps<{
  patient: PatientSummary | null;
  // A patient incorporated before is not chosen on this screen.
  patientEditable: boolean;
  lines: IncorporationLine[];
  drafts: LineDraft[];
  // The chosen patient's Requirements, which a row may continue.
  requirements: RequirementSummary[];
}>();
const emit = defineEmits<{ edit: [step: 'patient' | 'lines'] }>();

const MODES: Record<Exclude<RowDraft['mode'], 'continue'>, string> = {
  create: 'Nuevo', keep: 'Sin cambios', update: 'Actualizado',
};

function rowText(row: RowDraft): string {
  if (row.mode !== 'continue') return `${MODES[row.mode]}: ${requirementText(row)}`;
  const continued = props.requirements.find((requirement) => requirement.id === row.requirementId);

  return `Continúa: ${continued ? requirementText(continued) : 'un requerimiento del paciente'}`;
}
</script>
<template>
  <div>
    <template v-if="patient">
      <section-header
        icon="mdi-account"
        title="Paciente"
      >
        <v-btn
          v-if="patientEditable"
          size="small"
          variant="text"
          color="primary"
          @click="emit('edit', 'patient')"
        >
          Editar
        </v-btn>
      </section-header>
      <div class="d-flex flex-wrap ga-6 mt-2 mb-6">
        <info-field
          icon="mdi-account-outline"
          label="Paciente"
          :value="patient.name"
        />
        <info-field
          v-if="patient.dni"
          icon="mdi-card-account-details-outline"
          label="RUT"
          :value="patient.dni"
        />
        <info-field
          v-if="patient.address"
          icon="mdi-map-marker-outline"
          label="Dirección principal"
          :value="patient.address"
        />
        <info-field
          v-if="patient.representative"
          icon="mdi-account-supervisor-outline"
          label="Apoderado (cliente en Odoo)"
          :value="patient.representative"
        />
        <info-field
          icon="mdi-calendar-start"
          :label="patient.incorporatedBefore ? 'Incorporado desde' :
            'Kuido envía sus sesiones desde'"
          :value="formatDate(patient.goLiveOn)"
        />
      </div>
    </template>
    <section-header
      icon="mdi-clipboard-text-outline"
      title="Requerimientos"
    >
      <v-btn
        size="small"
        variant="text"
        color="primary"
        @click="emit('edit', 'lines')"
      >
        Editar
      </v-btn>
    </section-header>
    <ul class="lines mt-2">
      <li
        v-for="(line, index) in lines"
        :key="line.id"
        class="mb-3"
      >
        <div class="text-body-2 font-weight-medium">
          Línea {{ index + 1 }} · {{ line.productName ?? 'Producto sin nombre' }} ·
          {{ quantityText(line.quantity, line.uom) }}
        </div>
        <div
          v-if="drafts[index]?.dismissed"
          class="text-body-2 text-medium-emphasis"
        >
          Descartada: no se incorpora a Kuido.
        </div>
        <ul
          v-else
          class="rows"
        >
          <li
            v-for="row in drafts[index]?.rows ?? []"
            :key="row.key"
            class="text-body-2"
          >
            {{ rowText(row) }}
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
<style scoped>
.lines {
  list-style: none;
  padding: 0;
}

.rows {
  padding-left: 20px;
}
</style>
